import { createSelector } from '@reduxjs/toolkit';
import { CalendarDate, getLocalTimeZone, parseDate, today } from '@internationalized/date';

import { VisualAppState } from '../../reducer';
import { isRelativeDateRange, serializeFilters, toCalendarDate } from './utils';
import { OwnerType, RangeValue } from './types';
import { types as filterBarTypes } from '../../../../web-ui-jobs/src/ducks/filterBar';
import { pageDefault } from './constants';

export const globalState = (state: VisualAppState) => state;
export const visualFilters = (state: VisualAppState) => state.apps.visual.filterBar;
const ownerFilter = (state: VisualAppState) => state.apps.visual.filterBar.owner;
export const location = (state: VisualAppState) => state.router.location;
export const getUser = (state: VisualAppState) => state.user;

export const hasInit = createSelector(
  (state: VisualAppState) => state.apps.visual.filterBar.init,
  (init) => init === 'succeeded',
);

const visualDateValue = createSelector(visualFilters, (state) => state.date);

export const getUserId = createSelector(getUser, (user) => user.getUserID());

export const isDefaultFilters = createSelector([getUserId, visualFilters], (userId, filters) => (
  filters.date.start === filterBarTypes.RelativeDateRanges.last7Days &&
  filters.owner.value === userId &&
  filters.owner.type === 'user' &&
  filters.buildName.value === '' &&
  filters.buildName.searchValue === null &&
  filters.status.value.length === 0 &&
  filters.page.value === pageDefault
));

export const getSearchParams = createSelector(visualFilters, (state) => (
  JSON.stringify(serializeFilters(state))
));

export const ownerFilterValue = createSelector(
  [getUserId, ownerFilter],
  (userId, filterState): {type: OwnerType, value: string} => {
    if (filterState.value && filterState.type) {
      return {
        value: filterState.value,
        type: filterState.type,
      };
    }

    return {
      value: userId,
      type: 'user',
    };
  },
);

export const dateFilterValue = createSelector(visualDateValue,
  (dateValue): RangeValue<CalendarDate> => {
    let returnValue: RangeValue<CalendarDate>;

    // Parse relative dates
    if (isRelativeDateRange(dateValue.start)) {
      returnValue = {
        start: toCalendarDate(dateValue.start),
        end: today(getLocalTimeZone()),
      };
    } else {
      returnValue = {
        start: parseDate(dateValue.start),
        end: parseDate(dateValue.end),
      };
    }

    return returnValue;
  },
);

export const getNameFilter = (state: VisualAppState) => state.apps.visual.filterBar.buildName;

export const nameValue = createSelector(getNameFilter, (buildName) => (
  buildName.value
));

export const nameSearchValue = createSelector(getNameFilter, (buildName) => (
  buildName.searchValue
));

export const getStatusFilter = (state: VisualAppState) => state.apps.visual.filterBar.status;

export const statusValue = createSelector(getStatusFilter, (state) => (
  state.value
));

export const getProjectFilter = (state: VisualAppState) => state.apps.visual.filterBar.project;

export const projectValue = createSelector(getProjectFilter, (state) => (
  state.value ? [state.value] : null
));

export const getBranchFilter = (state: VisualAppState) => state.apps.visual.filterBar.branch;

export const branchValue = createSelector(getBranchFilter, (state) => (
  state.value ? [state.value] : null
));
export const pageValue = createSelector(
  (state: VisualAppState) => state.apps.visual.filterBar.page, (state) => state.value,
);
